import { useContext } from 'react'

import { UserContext } from '@doist/marketist'

import { Footer, Navbar } from 'components'
import { BodyBackgroundWhite } from 'components/body-background-white'
import FooterCTA from 'components/remote-work-guides/FooterCTA/FooterCTA'
import PostCard from 'components/remote-work-guides/PostCard/PostCard'
import SubscribeBox from 'components/remote-work-guides/SubscribeBox/SubscribeBox'
import { Seo } from 'components/Seo'
import { GetServerSidePropsContext } from 'next'
import Head from 'next/head'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import introImageUrl from 'public/assets/ogimages/en/og-image-remote-work-guides.jpg'
import { getCommonServerSideProps } from 'services'
import { RemoteWorkGuidesClient as contentful } from 'services/contentful/clients'
import { getOgImageURL } from 'services/ogimage'
import { RemoteGuides } from 'types/contentful'

/* Styles Import */
import css from './index.module.css'
import adaptive from 'styles/adaptive.module.css'

export type RemoteGuidesMainPageProps = {
    blogIndex: RemoteGuides.BlogIndex
}

export default function RemoteGuidesMainPage({ blogIndex }: RemoteGuidesMainPageProps) {
    const { isAuthenticated } = useContext(UserContext)

    const seoTitle = `${blogIndex.title} | Twist`
    return (
        <>
            <Head>
                <link
                    rel="alternate"
                    type="application/rss+xml"
                    title="The Remote Work Guides‘s RSS Feed"
                    href="/remote-work-guides/rss.xml"
                />
            </Head>

            <Seo
                path="/remote-work-guides"
                title={seoTitle}
                description={blogIndex.description}
                generateAlternateUrls={false}
                images={[{ url: getOgImageURL('og-image-remote-work-guides.jpg') }]}
                otherLanguages={[]}
            />

            <BodyBackgroundWhite />

            <Navbar isAuth={isAuthenticated} />

            <div>
                <div className={adaptive.container}>
                    <div className={css.introContainer}>
                        <div>
                            <h1>{blogIndex.title}</h1>
                            <p>{blogIndex.description}</p>
                            <SubscribeBox />
                        </div>

                        <img src={introImageUrl} />
                    </div>

                    <div className={css.gridContainer}>
                        {blogIndex.postsCollection.items.length > 0 ? (
                            <ul>
                                <li className={css.featuredPost}>
                                    <PostCard
                                        post={blogIndex.postsCollection.items[0]}
                                        featuredPost={true}
                                    />
                                </li>

                                {blogIndex.postsCollection.items.slice(1).map((post) => {
                                    return (
                                        <li key={post.slug}>
                                            <PostCard post={post} featuredPost={false} />
                                        </li>
                                    )
                                })}
                            </ul>
                        ) : (
                            <p>Uh-oh, no posts yet. Check back later.</p>
                        )}
                    </div>

                    <div className={css.subscribeContainer}>
                        <h2>Want up-to-date advice on running a remote team?</h2>
                        <SubscribeBox />
                    </div>
                </div>

                <FooterCTA />

                <Footer />
            </div>
        </>
    )
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
    const { locale, ...commonProps } = await getCommonServerSideProps(ctx)

    const blogIndex = await contentful.getBlogIndex(locale)

    return {
        props: {
            blogIndex,
            introImageUrl,
            ...commonProps,
            ...(await serverSideTranslations(locale, ['common'])),
        },
    }
}
